import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  View,
  ImageBackground,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
  Dimensions
} from 'react-native'
import {
  Icon,
  VFText,
  VFButton,
  QuickActionMenu
} from '@vfgroup-oneplatform/foundation/Components'

import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'

import UnlimitedContext from '../../Context'

import { BUNDLE_TYPE, getSelectedBundleData } from '../../Utils/bundle'

import Offer from '../Offer'
import VFTextWithLoader from '../VFTextWithLoader'
import CustomErrorScreen from '../CustomErrorScreen'

import styles from './CustomBundleConfigurator.Styles'

import { UhImages, getThemeImages } from '../../Themes'
import ReactNativeModal from 'react-native-modal'

const getActiveIndex = (entries, selected) => {
  const index = entries.findIndex(entry => entry.id === selected)
  return index >= 0 ? index : null
}

const getStylizedOptions = ({ elements, screenWidth, theme }) => {
  if (!Array.isArray(elements) || !elements.length) {
    return []
  } else {
    const count = elements.length
    const width = (screenWidth - (count > 1 ? 56 : 32)) / count
    return elements.map((el, index) => {
      return {
        ...el,
        containerStyle: [
          { width },
          styles.bgWhite(theme),
          styles.verticalPadding,
          // index === 0 ? null : { marginLeft: 12 }
        ]
      }
    })
  }
}

const CustomBundleConfigurator = ({
  goNext,
  getPrice,
  getFixAndTVs,
  getTariffPlans,
  getMemberSlots,
  updateCustomBundle,
  toggleIsCustomBundle
}) => {
  const { width } = useWindowDimensions()

  const [speedIndex, setSpeedIndex] = useState(null)
  const [slotsIndex, setSlotsIndex] = useState(null)

  const {
    data: {
      customTVs,
      bundleType,
      customSlots,
      customPlans,
      customSpeeds,
      customBundlePrice,
      customBundleOptions: CBO
    }
  } = useContext(UnlimitedContext)

  useEffect(() => {
    Promise.all([getFixAndTVs({}), getTariffPlans({}), getMemberSlots({})])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (bundleType === BUNDLE_TYPE.custom && CBO.plan && CBO.speed) {
      getPrice({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleType, CBO.plan, CBO.tv, CBO.slots, CBO.speed])

  useEffect(() => {
    if (Array.isArray(customSlots.data) && slotsIndex === null) {
      setSlotsIndex(getActiveIndex(customSlots.data, CBO.slots))
    }
  }, [customSlots.data, CBO.slots, slotsIndex])

  useEffect(() => {
    if (Array.isArray(customSpeeds.data) && speedIndex === null) {
      setSpeedIndex(getActiveIndex(customSpeeds.data, CBO.speed))
    }
  }, [customSpeeds.data, CBO.speed, speedIndex])

  const theme = useTheme()

  const stylizedCustomSlots = getStylizedOptions({
    elements: customSlots.isLoading ? Array(3).fill({}) : customSlots.data,
    screenWidth: width,
    theme: theme
  })

  const stylizedCustomSpeeds = getStylizedOptions({
    elements: customSpeeds.isLoading ? Array(3).fill({}) : customSpeeds.data,
    screenWidth: width,
    theme: theme
  })

  const handleTVClick = id =>
    updateCustomBundle({ tv: id === CBO.tv ? '' : id })

  const handleOfferClick = id => {
    if (id === CBO.plan) {
      return
    }
    updateCustomBundle({ plan: id })
  }

  const handleSpeedSelect = index => {
    const selectedSpeed = index === null ? null : customSpeeds.data[index].id
    updateCustomBundle({ speed: selectedSpeed })
    setSpeedIndex(index)
  }

  const handleSlotSelect = index => {
    const selectedSlot = index === null ? null : customSlots.data[index].id
    updateCustomBundle({ slots: selectedSlot })
    setSlotsIndex(index)
  }

  const handleBackClick = () => toggleIsCustomBundle(false)

  const isButtonDisabled =
    customPlans.isLoading ||
    customPlans.isError ||
    customTVs.isLoading ||
    customTVs.isError ||
    customSlots.isLoading ||
    customSlots.isError ||
    customSpeeds.isLoading ||
    customSpeeds.isError ||
    customBundlePrice.isLoading ||
    customBundlePrice.isError ||
    CBO.speed === null ||
    CBO.plan === null

  const plansArr = customPlans.isLoading
    ? Array(2).fill({ recommended: false })
    : customPlans.data

  const tvsArr = customTVs.isLoading
    ? Array(3).fill({ recommended: false })
    : customTVs.data

  const [height, setHeight] = useState(100)

  const update = (e) => {
    console.log(e);
    setHeight(e)
  }

  return (
    <View style={styles.section}>
      {/* Offer */}
      <VFText i18nKey="unlimited_choose_plan_title" style={styles.title} />
      {customPlans.isError ? (
        <CustomErrorScreen onPress={() => getTariffPlans({})} />
      ) : (
        <>
          {plansArr.map(plan => (
            <View style={styles.bottom}>
              <View
                style={
                  plan.id === CBO.plan
                    ? styles.selectedPlan
                    : styles.planWrapper
                }
              >
                <Offer
                  type="plan"
                  offer={plan}
                  key={plan.id}
                  onPress={handleOfferClick}
                  isLoading={customPlans.isLoading}
                  isSelected={plan.id === CBO.plan}
                  stylesArray={[styles.roundedBorders(theme)]}
                />
              </View>
            </View>
          ))}
        </>
      )}
      {/* Speed */}
      <VFText i18nKey="unlimited_choose_speed_title" style={styles.title} />
      <VFText
        i18nKey="unlimited_choose_speed_sub"
        style={styles.subTitle(theme)}
      />
      {customSpeeds.isError ? (
        <CustomErrorScreen onPress={() => getFixAndTVs({})} />
      ) : (
        <CustomSelectionList
          activeIndex={speedIndex}
          customItem={<SpeedOption />}
          options={stylizedCustomSpeeds}
          isLoading={customSpeeds.isLoading}
          onSelectOption={handleSpeedSelect}
        />
      )}
      {/* TV */}
      <VFText i18nKey="unlimited_choose_tv_title" style={styles.title} />
      <VFText
        i18nKey="unlimited_choose_tv_sub"
        style={styles.subTitle(theme)}
      />
      {customTVs.isError ? (
        <CustomErrorScreen onPress={() => getFixAndTVs({})} />
      ) : (
        <View>
          {tvsArr.map(tv => (
            <View style={styles.bottom}>
              <View
                style={
                  tv.id === CBO.tv ? styles.selectedPlan : styles.planWrapper
                }
              >
                <Offer
                  type="tv"
                  offer={tv}
                  key={tv.id}
                  style={styles.bottom}
                  onPress={handleTVClick}
                  isError={customTVs.isError}
                  isSelected={tv.id === CBO.tv}
                  isLoading={customTVs.isLoading}
                  stylesArray={[styles.roundedBorders(theme)]}
                />
              </View>
            </View>
          ))}
        </View>
      )}
      {/* Slots */}
      <VFText i18nKey="unlimited_choose_slots_title" style={styles.title} />
      {customSlots.isError ? (
        <CustomErrorScreen onPress={() => getMemberSlots({})} />
      ) : (
        <>
          <CustomSelectionList
            activeIndex={slotsIndex}
            customItem={<SlotOption />}
            options={stylizedCustomSlots}
            isLoading={customSlots.isLoading}
            onSelectOption={handleSlotSelect}
          />
          <UnselectButton
            show={slotsIndex !== null}
            isLoading={customSlots.isLoading}
            text="unlimited_unselect_members"
            onPress={() => handleSlotSelect(null)}
          />
        </>
      )}
      <ViewCart />
      {/* Buttons */}
      <VFButton
        onPress={goNext}
        disabled={isButtonDisabled}
        title="unlimited_next_step_button"
      />
      <TouchableOpacity
        onPress={handleBackClick}
        style={[styles.row, styles.backBtn]}
      >
        <VFText i18nKey="unlimited_go_back_to_offers" style={styles.backTxt} />
      </TouchableOpacity>
      <PriceError
        show={customBundlePrice.isError}
        onClose={() => getPrice({})}
      />
    </View>
  )
}

const getSlotCount = val => {
  const count = val ? val.split(' ')[0] : ''
  return count ? parseInt(count, 10) : 0
}

const getSlotImage = (slots, themedImages) => {
  const parsedSlot = getSlotCount(slots)
  switch (parsedSlot) {
    case 1:
      return themedImages.two_slots
    case 2:
      return themedImages.three_slots
    case 3:
      return themedImages.four_slots
    default:
      return ''
  }
}

const SlotOption = props => {
  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)
  const isActive = props.activeIndex === props.index

  const handlePress = () => {
    if (props.isLoading) {
      return
    }
    props.onPress(props.index)
  }

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={handlePress}
      style={[
        styles.roundedBorders,
        isActive ? styles.selectedPlan : styles.optionWrapper,
        ...props.option.containerStyle
      ]}
    >
      <View style={styles.row}>
        {props.isLoading ? (
          <VFTextWithLoader
            loaderWidth={76}
            loaderHeight={76}
            style={styles.bottom}
            isLoading={props.isLoading}
          />
        ) : (
          <Icon
            size={76}
            type="image"
            style={styles.bottom}
            name={getSlotImage(props.option.slots, themedImages)}
          />
        )}
        <VFTextWithLoader
          isLoading={props.isLoading}
          i18nKey="unlimited_slots_count"
          placeHolders={[props.option.slots]}
          style={[
            styles.centerText,
            isActive ? styles.activeOptionText : styles.optionText
          ]}
        />
      </View>
    </TouchableOpacity>
  )
}

const SpeedOption = props => {
  const isActive = props.activeIndex === props.index

  const handlePress = () => {
    if (props.isLoading) {
      return
    }
    props.onPress(props.index)
  }

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={handlePress}
      style={[
        styles.roundedBorders,
        isActive ? styles.selectedPlan : styles.optionWrapper,
        ...props.option.containerStyle
      ]}
    >
      <View style={styles.row}>
        <VFTextWithLoader
          isLoading={props.isLoading}
          style={[
            styles.centerText,
            isActive ? styles.activeOptionText : styles.optionText
          ]}
          i18nKey={props.option.title}
        />
      </View>
    </TouchableOpacity>
  )
}

const ViewCart = () => {
  const {
    data: {
      customTVs,
      bundleType,
      atlBundles,
      pegaBundles,
      customPlans,
      customSlots,
      customSpeeds,
      selectedBundle,
      customBundlePrice,
      customBundleOptions: CBO
    }
  } = useContext(UnlimitedContext)

  const bundleData = getSelectedBundleData({
    customTVs,
    bundleType,
    atlBundles,
    pegaBundles,
    customPlans,
    customSlots,
    customSpeeds,
    selectedBundle,
    customBundlePrice,
    customBundleOptions: CBO
  })

  const [show, setShow] = useState(false)

  const toggleShow = () => setShow(prev => !prev)

  const theme = useTheme()
  const themedImages = getThemeImages(theme.name)

  const noOptionsSelected = !CBO.plan && !CBO.speed && !CBO.tv && !CBO.slots

  const selectedPlan = customPlans.data.find(plan => plan.id === CBO.plan)

  const selectedSpeed = customSpeeds.data?.find(speed => speed.id === CBO.speed)

  const selectedTV = customTVs.data?.find(tv => tv.id === CBO.tv)

  const planLines = [
    {
      label: <VFText i18nKey="unlimited_national_calls" />,
      value: (
        <VFText
          i18nKey="unlimited_minutes"
          placeHolders={[selectedPlan?.details?.minutes]}
        />
      )
    },
    {
      label: <VFText i18nKey="unlimited_national_sms" />,
      value: (
        <VFText
          i18nKey="unlimited_sms_count"
          placeHolders={[selectedPlan?.details?.sms]}
        />
      )
    },
    {
      label: <VFText i18nKey="unlimited_internet" />,
      value: (
        <VFText
          i18nKey="%1$s"
          placeHolders={[selectedPlan?.details?.internet]}
        />
      )
    }
  ]

  const internetLines = [
    {
      label: <VFText i18nKey="unlimited_speed" />,
      value: <VFText i18nKey="%1$s" placeHolders={[selectedSpeed?.title]} />
    }
  ]

  const groupLines = [
    {
      label: (
        <VFText
          i18nKey="unlimited_added_members"
          placeHolders={[getSlotCount(CBO.slots)]}
        />
      ),
      value: null
    }
  ]

  return (
    <>
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={toggleShow}
        style={[styles.viewCartBanner, styles.roundedBorders]}
      >
        <ImageBackground
          resizeMode="cover"
          style={styles.image}
          source={UhImages.card_details}
        >
          <View style={styles.viewCartText}>
            <VFText
              i18nKey="unlimited_view_cart_title"
              style={[styles.cartTitle, styles.white]}
            />
            <View style={styles.bottomRow}>
              <VFText i18nKey="unlimited_view_cart_cta" style={styles.white} />
              <Icon type="image" name={UhImages.chevron_white} />
            </View>
          </View>
        </ImageBackground>
      </TouchableOpacity>
      {Platform.OS === 'web' && Dimensions.get('window').width > 700 ?
        <ReactNativeModal
          isVisible={show}
          backdropOpacity={0.5}
          onSwipeComplete={toggleShow}
          onBackdropPress={toggleShow}
          propagateSwipe
          hideModalContentWhileAnimating
          hardwareAccelerated
          backdropTransitionOutTiming={0}
          style={{
            justifyContent: 'flex-end',
            margin: '0 !important',
            minWidth: 1920,
            maxWidth: 1920,
          }}
        >
          <View
            style={{
              minWidth: '600px',
              maxWidth: '600px',
              height: 'auto',
              backgroundColor: theme.colors.quickActionGradientColors.secondary,
              margin: 'auto',
              padding: '20px',
              paddingTop: null,
              borderRadius: "7px",
            }}
            onStartShouldSetResponder={() => true}>
            {noOptionsSelected ? (
              <View style={styles.body}>
                <View style={styles.space}>
                  <VFText
                    i18nKey="unlimited_cart_details_subtitle"
                    style={styles.cardSubtitle}
                  />
                  <VFText
                    i18nKey="unlimited_cart_details_none_selected"
                    style={styles.noneSelected}
                  />
                </View>
              </View>
            ) : (
              <View style={styles.body}>
                <View style={styles.space}>
                  <VFText
                    i18nKey="unlimited_cart_details_subtitle"
                    style={styles.cardSubtitle}
                  />
                </View>
                {/* Desc */}
                <View style={[styles.details(theme), styles.space]}>
                  {CBO.plan ? (
                    <ViewCartEntry
                      withDivider
                      title={CBO.plan}
                      lines={planLines}
                      icon={themedImages.mobile}
                      subTitle="unlimited_mobile_plan"
                    />
                  ) : null}
                  {CBO.speed ? (
                    <ViewCartEntry
                      withDivider
                      lines={internetLines}
                      icon={themedImages.offer}
                      subTitle="unlimited_internet"
                      title="unlimited_vf_internet"
                    />
                  ) : null}
                  {CBO.tv ? (
                    <ViewCartEntry
                      withDivider
                      lines={[]}
                      icon={themedImages.tv}
                      title={selectedTV?.name}
                      subTitle="unlimited_tv_name"
                    />
                  ) : null}
                  {CBO.slots ? (
                    <ViewCartEntry
                      lines={groupLines}
                      icon={themedImages.members}
                      subTitle="unlimited_your_group"
                      title="unlimited_my_members_short"
                    />
                  ) : null}
                </View>
                <View style={styles.space}>
                  <View style={[styles.entry, styles.spaceBetween]}>
                    <VFText
                      style={styles.activeOptionText}
                      i18nKey="unlimited_summary_recurring"
                      placeHolders={[bundleData.productTerms?.amount]}
                    />
                    <VFText
                      style={styles.activeOptionText}
                      i18nKey="unlimited_summary_amount"
                      placeHolders={[bundleData.price?.value?.toString()]}
                    />
                  </View>
                </View>
              </View>
            )}
          </View>
        </ReactNativeModal>

        : <QuickActionMenu
          type="secondary"
          isVisible={show}
          toggleModal={toggleShow}
          headerStyle={styles.bottomPadding}
          title="unlimited_cart_details_title"
          containerStyle={styles.containerStyles}
        >
          {noOptionsSelected ? (
            <View style={styles.body}>
              <View style={styles.space}>
                <VFText
                  i18nKey="unlimited_cart_details_subtitle"
                  style={styles.cardSubtitle}
                />
                <VFText
                  i18nKey="unlimited_cart_details_none_selected"
                  style={styles.noneSelected}
                />
              </View>
            </View>
          ) : (
            <View style={styles.body}>
              <View style={styles.space}>
                <VFText
                  i18nKey="unlimited_cart_details_subtitle"
                  style={styles.cardSubtitle}
                />
              </View>
              {/* Desc */}
              <View style={[styles.details(theme), styles.space]}>
                {CBO.plan ? (
                  <ViewCartEntry
                    withDivider
                    title={CBO.plan}
                    lines={planLines}
                    icon={themedImages.mobile}
                    subTitle="unlimited_mobile_plan"
                  />
                ) : null}
                {CBO.speed ? (
                  <ViewCartEntry
                    withDivider
                    lines={internetLines}
                    icon={themedImages.offer}
                    subTitle="unlimited_internet"
                    title="unlimited_vf_internet"
                  />
                ) : null}
                {CBO.tv ? (
                  <ViewCartEntry
                    withDivider
                    lines={[]}
                    icon={themedImages.tv}
                    title={selectedTV?.name}
                    subTitle="unlimited_tv_name"
                  />
                ) : null}
                {CBO.slots ? (
                  <ViewCartEntry
                    lines={groupLines}
                    icon={themedImages.members}
                    subTitle="unlimited_your_group"
                    title="unlimited_my_members_short"
                  />
                ) : null}
              </View>
              <View style={styles.space}>
                <View style={[styles.entry, styles.spaceBetween]}>
                  <VFText
                    style={styles.activeOptionText}
                    i18nKey="unlimited_summary_recurring"
                    placeHolders={[bundleData.productTerms?.amount]}
                  />
                  <VFText
                    style={styles.activeOptionText}
                    i18nKey="unlimited_summary_amount"
                    placeHolders={[bundleData.price?.value?.toString()]}
                  />
                </View>
              </View>
            </View>
          )}
        </QuickActionMenu>}
    </>
  )
}

const ViewCartEntry = ({ icon, title, subTitle, lines, withDivider }) => {
  return (
    <View style={withDivider ? styles.vceDivider : ''}>
      <View style={styles.entry}>
        <Icon size={40} name={icon} type="image" style={styles.gap} />
        <View style={styles.flex}>
          <VFText i18nKey={subTitle} style={styles.vceSubTitle} />
          <VFText i18nKey={title} style={styles.vceTitle} />
          {/* Lines */}
          {lines &&
            lines.map(line => (
              <View style={[styles.entry, styles.spaceBetween]}>
                <View>{line.label}</View>
                <View>{line.value}</View>
              </View>
            ))}
        </View>
      </View>
    </View>
  )
}

const UnselectButton = ({ show, text, onPress, isLoading }) => {
  if (!show) {
    return null
  }
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={1}
      style={[styles.row, styles.unselect]}
    >
      <VFTextWithLoader
        i18nKey={text}
        style={styles.text}
        isLoading={isLoading}
      />
    </TouchableOpacity>
  )
}

const PriceError = ({ show, onClose }) => {
  return (
    <QuickActionMenu
      type="secondary"
      isVisible={show}
      toggleModal={onClose}
      title="unlimited_order_error_modal"
      headerStyle={styles.bottomPadding}
      containerStyle={styles.containerStyles}
    >
      <CustomErrorScreen onPress={onClose} />
    </QuickActionMenu>
  )
}

const CustomSelectionList = ({
  options,
  isLoading,
  customItem,
  activeIndex,
  onSelectOption
}) => {
  return (
    <View style={[styles.select,
    Platform.select({
      web: {
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 8

      }
    })
    ]}>
      {options.map((option, index) =>
        React.cloneElement(customItem, {
          index,
          option,
          isLoading,
          activeIndex,
          onPress: onSelectOption
        })
      )}
    </View>
  )
}

CustomSelectionList.propTypes = {
  options: PropTypes.array,
  isLoading: PropTypes.bool,
  customItem: PropTypes.element,
  activeIndex: PropTypes.number,
  onSelectOption: PropTypes.func
}

PriceError.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func
}

UnselectButton.propTypes = {
  show: PropTypes.bool,
  text: PropTypes.string,
  onPress: PropTypes.func,
  isLoading: PropTypes.bool
}

ViewCartEntry.propTypes = {
  icon: PropTypes.any,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  withDivider: PropTypes.bool,
  lines: PropTypes.arrayOf(
    PropTypes.objectOf({
      label: PropTypes.node,
      value: PropTypes.node
    })
  )
}

CustomBundleConfigurator.propTypes = {
  goNext: PropTypes.func,
  getPrice: PropTypes.func,
  getFixAndTVs: PropTypes.func,
  getTariffPlans: PropTypes.func,
  getMemberSlots: PropTypes.func,
  updateCustomBundle: PropTypes.func,
  toggleIsCustomBundle: PropTypes.func
}

SlotOption.propTypes = {
  index: PropTypes.number,
  onPress: PropTypes.func,
  isLoading: PropTypes.bool,
  activeIndex: PropTypes.number,
  option: PropTypes.objectOf({
    id: PropTypes.string,
    slots: PropTypes.string,
    containerStyle: PropTypes.array
  })
}

SpeedOption.propTypes = {
  index: PropTypes.number,
  onPress: PropTypes.func,
  isLoading: PropTypes.bool,
  activeIndex: PropTypes.number,
  option: PropTypes.objectOf({
    id: PropTypes.string,
    oid: PropTypes.string,
    value: PropTypes.string,
    title: PropTypes.string,
    containerStyle: PropTypes.array
  })
}

export default CustomBundleConfigurator
